<template>
    <div>
        <div class="card border-primary mb-3">
            <h5 class="card-header text-success"><i class="fas fa-user-edit mr-2"></i>{{model.userName}}</h5>
            <div v-if="saving" class="loader"></div>
            <div v-show="!saving" class="card-body">
                <div class="row">
                    <div class="form-group col-sm-12 col-md-5">
                        <label v-if="!create" class="col-form-label font-weight-bold required" :for="'userName' + _uid">{{ $t('userName') }}</label>
                        <input v-if="!create" type="text"
                               autocomplete="username"
                               class="form-control"
                               :readonly="false"
                               v-model="model.userName"
                               v-validate="'required|max:256'"
                               :data-vv-as="$t('userName')"
                               :name="'userName' + _uid"
                               :id="'userName' + _uid" />
                        <span class="text-danger" v-if="errors.has('userName' + _uid)">{{ errors.first('userName' + _uid) }}</span>
                        <label class="col-form-label font-weight-bold required" :for="'email' + _uid">{{ $t('email') }}</label>
                        <input type="email"
                               autocomplete="new-email"
                               class="form-control"
                               :readonly="false"
                               v-model="model.email"
                               v-validate="'required|email'"
                               :data-vv-as="$t('email')"
                               @keyup.stop="onEmailTyping"
                               :name="'email' + _uid"
                               :id="'email' + _uid" />
                        <span class="text-danger" v-if="errors.has('email' + _uid)">{{ errors.first('email' + _uid) }}</span>
                        <div class="text-info radio-message mt-2">
                            <i class="fa fa-exclamation-circle"></i>
                            {{ $t("registerEmailInfo") }}
                        </div>
                    </div>

                    <div class="col-form-label form-group col-sm-12 col-md-5">
                        <label class="font-weight-bold" :for="'newPassword' + _uid">
                            {{ isNew ? $t('password') : $t('changePassword.newPassword')}}
                        </label>
                        <div class="input-group">
                            <input
                                :type="showPassword ? 'tetx' : 'password'"
                                autocomplete="new-password"
                                class="form-control"
                                v-model="model.newPassword"
                                :data-vv-as="$t('changePassword.newPassword')"
                                :name="'newPassword' + _uid"
                                :id="'newPassword' + _uid"
                                v-validate="'upCaseNum'"
                                aria-describedby="passwordHelp" />
                            <div class="input-group-append">
                                <button class="btn btn-link-secondary m-0" type="button" @click.stop="showPassword=!showPassword">
                                   <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" aria-hidden="true"></i>
                                </button>
                            </div>
                        </div>
                        <span class="text-danger" v-if="errors.has('newPassword' + _uid)">{{ errors.first('newPassword' + _uid) }}</span>
                        <small id="passwordHelp" v-else class="form-text text-muted">{{$t('registerPasswordHelp')}}</small>
                    </div>

                    <div class="form-group col-sm-12 col-md-2">
                        <label class="col-form-label font-weight-bold" :for="'deleted' + _uid"></label>
                        <vs-checkbox v-model="model.deleted"
                            :name="'deleted' + _uid"
                            :id="'deleted' + _uid">
                            {{ $t('person.gridHeaders.deleted') }}
                        </vs-checkbox>
                    </div>
                </div>

                <div class="row ml-1 mr-1">
                    <kendo-datasource ref="userRolesDatasource"
                        :transport="transportSettings"
                        :server-filtering="false"
                        :server-paging="false"
                        :schema-model-id="'id'"
                        :schema-model-fields="roleSchemaModel"
                        :error="dataSourceError"
                        :requestEnd="datasorceRequestEnd">
                    </kendo-datasource>

                    <label class="col-form-label font-weight-bold" :for="'userRoles' + _uid">{{ $t('administration.userRoles.title') }}</label>
                    <kendo-multiselect :data-source-ref="'userRolesDatasource'"
                        v-model="model.selectedRoles"
                        :data-text-field="'name'"
                        :data-value-field="'id'"
                        :name="'userRoles' + _uid"
                        :id="'userRoles' + _uid"
                        :placeholder="$t('choose')"
                        :no-data-template="$t('missingData')"
                        class="form-control"
                        style="width: 100%; height: auto;">
                    </kendo-multiselect>
                </div>

                <hr />

                <div class="row" v-if="model.moodleUserAccountDetails.userSyncedWithMoodle">
                    <div class="form-group col-sm-12 col-md-6">
                        <div class="text-success radio-message-success mt-2">
                            <i class="fa fa-exclamation-circle mr-2"></i>
                            {{ $t('person.accountSyncedWithMoodle') }}
                        </div>
                    </div>
                    <div class="form-group col-sm-12 col-md-6">
                        <vs-checkbox v-model="model.deleteMoodleSync"
                                     :name="'deleteMoodleSync' + _uid"
                                     :id="'deleteMoodleSync' + _uid">
                            {{ $t('person.deleteMoodleSync') }}
                        </vs-checkbox>
                    </div>
                </div>

                <div class="row" v-else-if="model.moodleUserAccountDetails.userExistingInMoodleButNotSynced">
                    <div class="form-group col-sm-12 col-md-8">
                        <div class="text-info radio-message-info mt-2">
                            <i class="fa fa-exclamation-circle mr-2"></i>
                            {{ $t('person.userExistingInMoodleButNotSynced', { email: model.email }) }}
                        </div>
                    </div>
                    <div class="form-group col-sm-12 col-md-4 text-right">
                        <button class="ml-0 btn btn-outline-info"
                                @click.stop="syncAccountWithMoodle"
                                :disabled="sync">
                            <i class="fas fa-spinner fa-spin" v-if="sync"></i>
                            <i class="fas fa-sync" v-else></i>
                            {{ $t('buttons.syncWithMoodle.btnText')}}
                        </button>
                    </div>
                </div>

                <div class="row" v-if="!isNew">
                    <div class="form-group col-sm-12 col-md-3">
                        <label class="col-form-label font-weight-bold" :for="'canSignDocument' + _uid"></label>
                        <vs-checkbox v-model="model.canSignDocument"
                            :name="'canSignDocument' + _uid"
                            :id="'canSignDocument' + _uid">
                            {{ $t('administration.user.canSignDocument') }}
                        </vs-checkbox>
                    </div>

                    <div class="form-group col-sm-12 col-md-9">
                        <div class="font-weight-bold">{{$t('administration.user.signatureImage')}}</div>
                        <table class="table table-hover mt-2" v-if="model.signatureImageDocuments">
                            <tbody>
                                <tr v-for="(item, index) in model.signatureImageDocuments" :key="index">
                                    <td class="py-0 align-middle">{{ item.description }}</td>
                                    <td class="py-0 align-middle">{{ item.fileName }}</td>
                                    <td class="text-right py-0">
                                        <file-crud
                                            :file="item"
                                            :showDescription="false"
                                            :type="item.docTypeCode"
                                            hide-file-name
                                            accept-filter="image/png"
                                            @removeFile="onRemoveSignatureImageFile"
                                        >
                                        </file-crud>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-if="!model.signatureImageDocuments.length  || !model.signatureImageDocuments.length > 1">
                            <button class="btn btn-primary ml-0" @click="addSignatureImageAtachment">
                                <i class="fa fa-plus"></i>
                                {{$t('add')}}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card-footer text-right">
                <div class="btn-group" role="group" aria-label="User edit buttons">
                    <button type="button" class="btn btn-link text-success" @click.stop="onSave"><i class="fas fa-save mr-2"></i>{{ $t('buttons.save.btnText')}}</button>
                    <button type="button" class="btn btn-link text-danger" @click.stop="onCancel"><i class="fas fa-ban mr-2"></i>{{ $t('buttons.cancel')}}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { AspNetUser, MoodleUserCreateModel, File } from '@/models/models';
    import { Culture } from '@/models/enums';
    import userService from '@/services/user.service';
    import FileCrud from '@/components/common/FileCRUD';

    export default {
        inject: ['$validator'],
        components: { FileCrud },
        props: {
            value: {
                type: AspNetUser,
                required: false
            },
            create: {
                type: Boolean,
                default: false
            },
        },
        data() {
            return {
                saving: false,
                model: this.value,
                sync: false,
                transportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + `/api/roles/getAllModels`,
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                parameterMap: function (options, operation) {
                    if (operation !== 'read' && options.models) {
                        return { models: kendo.stringify(options.models) }
                    } else {
                        return kendo.stringify(options);
                    }
                },
                roleSchemaModel: {
                    id: { name: 'id', from: 'id', type: 'string', nullable: false },
                    name: { name: 'name', from: 'name', type: 'string', nullable: true },
                    description: { name: 'description', from: 'description', type: 'string', nullable: true }
                },
                showPassword: false
            }
        },
        methods: {
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            datasorceRequestEnd(e) {

            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            onCancel() {
                this.$emit("cancel");
            },
            onSave() {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        kendo.confirm(`${this.$t('person.confirmations.edit')} '${this.model.userName}'?`)
                            .then(function () {
                                this.doSave();
                            }.bind(this));
                    }
                });
            },
            doSave() {
                if (this.create && this.isNew) {
                    this.createUser();
                } else {
                    this.updateUser();
                }
            },
            createUser() {
                this.saving = true;

                userService
                    .create(this.model)
                    .then(response => {
                        this.$emit("save");

                        this.$vs.notify({
                            title: this.$t('buttons.create.btnText'),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration,
                        })
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('buttons.create.btnText'),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    })
                    .then(() => { this.saving = false; });
            },
            async updateUser() {
                this.saving = true;

                let data = new FormData();
                data.append('id', this.model.id);
                data.append('userName', this.model.userName);
                data.append('email', this.model.email);
                data.append('newPassword', this.model.newPassword);
                data.append('deleted', this.model.deleted);
                data.append('canSignDocument', this.model.canSignDocument);
                data.append('deleteMoodleSync', this.model.deleteMoodleSync);

                // selected roles
                for (let index = 0; index < this.model.selectedRoles.length; index++) {
                    data.append(`selectedRoles[${index}]`, this.model.selectedRoles[index]);
                }

                // files
                if (this.model.signatureImageDocuments.length) {
                    for (let index = 0; index < this.model.signatureImageDocuments.length; index++) {
                        const attachment = this.model.signatureImageDocuments[index];
                        if (attachment.id) {
                            data.append(`signatureImageDocuments[${index}].id`, attachment.id);
                            data.append(`signatureImageDocuments[${index}].fileName`, attachment.fileName);
                        } else {
                            data.append(`files`, attachment.content);
                        }
                    }
                }

                userService
                    .update(data)
                    .then(response => {
                        this.$emit("save");

                        this.$vs.notify({
                            title: this.$t('buttons.edit.btnText'),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration,
                        })
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('buttons.edit.btnText'),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    })
                    .then(() => { this.saving = false; });
            },
            onEmailTyping() {
                if (this.create) {
                    this.model.userName = this.model.email;
                }
            },
            syncAccountWithMoodle() {
                const model = new MoodleUserCreateModel(
                    {
                        idnumber: this.model.id,
                        userName: this.model.email,
                        email: this.model.email,
                        firstName: this.model.firstName,
                        lastName: this.model.lastName,
                        middleName: this.model.middleName,
                        description: this.model.description,
                        createpassword: 1
                    });

                this.sync = true;

                this.$http
                    .post('/api/moodle/SyncUserFromIsupo', model)
                    .then(response => {
                        this.$emit("save");

                        this.$vs.notify({
                            title: this.$t('buttons.syncWithMoodle.btnText'),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration,
                        })
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('buttons.syncWithMoodle.btnText'),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    }).then(() => { this.sync = false });
            },
        addSignatureImageAtachment() {
			this.model.signatureImageDocuments.push(new File());
		},
		onRemoveSignatureImageFile(_id) {
			this.model.signatureImageDocuments = this.model.signatureImageDocuments.filter(x => x._id !== _id);
		}
        },
        computed: {
            token: function () {
                return this.$store.getters.authToken;
            },
            isEnVersion() {
                return this.$store.getters.currentCulture === Culture.EN;
            },
            isNew() {
                if (!this.model.id && this.create === true) {
                    return true;
                }

                return false;
            }
        },
        watch: {
            value: function (val) {
                if (val) {
                    this.model = new AspNetUser(this.value);
                }
            }
        }
    }
</script>

<style scoped>
    input[readonly] {
        background-color: whitesmoke;
    }

    textarea[readonly] {
        background-color: whitesmoke;
    }
</style>
